import React, {useState} from 'react';
import {useQuery, useMutation, gql, MutationFunctionOptions} from '@apollo/client';
import {Link, useParams} from 'react-router-dom';
import {useSnackbar} from 'notistack';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
  Button,
  Stack,
  Tooltip,
} from '@mui/material';
import {ArrowBack} from '@mui/icons-material';
import {COGNITO_USER_FRAGMENT} from '../../graphql/fragments';
import {IdentityHistory} from './IdentityHistory';
import {UpdateEmail} from './UpdateEmail';
import Loading from '../../components/Loading/Loading';
import {ErrorPage} from '../ErrorPage';
import {themeColors} from '../../theme/Theme';
import {
  GetIdentityUser,
  GetIdentityUserVariables,
} from '../../schema-types/GetIdentityUser';
import {
  SetIdentityUserAccess,
  SetIdentityUserAccessVariables,
} from '../../schema-types/SetIdentityUserAccess';
import {
  UpdateIdentityEmail,
  UpdateIdentityEmailVariables,
} from '../../schema-types/UpdateIdentityEmail';
import {
  ResetIdentityPassword,
  ResetIdentityPasswordVariables,
} from '../../schema-types/ResetIdentityPassword';
import {
  ResetIdentityMfa,
  ResetIdentityMfaVariables,
} from '../../schema-types/ResetIdentityMfa';
import {
  IdentityUserSetGroup,
  IdentityUserSetGroupVariables,
} from '../../schema-types/IdentityUserSetGroup';
import {
  IdentityGroupActionEnum,
  IdentityGroupEnum,
  UserStatusEnum,
} from '../../schema-types/globalTypes';
import {Panel} from '../../components/Panel/Panel';
import {ColoredChip} from '../../components/EditorGrid/GridRenderComponents';

const GROUP_LABELS: Record<IdentityGroupEnum, string> = {
  [IdentityGroupEnum.CLIENT]: 'Client',
  [IdentityGroupEnum.ONEVIEW]: 'Oneview (ignore)',
  [IdentityGroupEnum.ADVISER]: 'Adviser',
  [IdentityGroupEnum.ADMIN]: 'Standard Administrator',
};

function getGroupLabel(group: string) {
  if (group.toLocaleLowerCase().includes('oneview')) {
    return 'OneView (ignore))';
  }
  return group;
}

export const GET_IDENTITY_USER_QUERY = gql`
  ${COGNITO_USER_FRAGMENT}
  query GetIdentityUser($uuidOrEmail: String!) {
    getIdentityUser(uuidOrEmail: $uuidOrEmail) {
      user {
        ...CognitoUserFragment
      }
      ok
      errors
    }
  }
`;

export const SET_IDENTITY_USER_ACCESS_MUTATION = gql`
  ${COGNITO_USER_FRAGMENT}
  mutation SetIdentityUserAccess($enable: Boolean!, $subId: String!) {
    setIdentityUserAccess(enable: $enable, subId: $subId) {
      user {
        ...CognitoUserFragment
      }
      ok
      errors
    }
  }
`;

export const RESET_IDENTITY_PASSWORD_MUTATION = gql`
  mutation ResetIdentityPassword($subId: String!) {
    resetIdentityPassword(subId: $subId) {
      ok
      errors
    }
  }
`;

export const RESET_IDENTITY_MFA_MUTATION = gql`
  ${COGNITO_USER_FRAGMENT}
  mutation ResetIdentityMfa($subId: String!) {
    resetIdentityMfa(subId: $subId) {
      user {
        ...CognitoUserFragment
      }
      ok
      errors
    }
  }
`;

export const IDENTITY_USER_SET_GROUP_MUTATION = gql`
  ${COGNITO_USER_FRAGMENT}
  mutation IdentityUserSetGroup(
    $subId: String!
    $group: IdentityGroupEnum!
    $action: IdentityGroupActionEnum!
  ) {
    identityUserSetGroup(subId: $subId, group: $group, action: $action) {
      user {
        ...CognitoUserFragment
      }
      ok
      errors
    }
  }
`;

export const useToggleUserAccess = () => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [_onToggleAccess, {data: toggleAccessData, loading: toggleAccessLoading}] =
    useMutation<SetIdentityUserAccess, SetIdentityUserAccessVariables>(
      SET_IDENTITY_USER_ACCESS_MUTATION,
      {
        refetchQueries: [GET_IDENTITY_USER_QUERY],
      }
    );

  const onToggleAccess = async (
    options?: MutationFunctionOptions<
      SetIdentityUserAccess,
      SetIdentityUserAccessVariables
    >
  ) => {
    closeSnackbar();
    const enable = options?.variables?.enable;
    const action = enable ? 'Enabling' : 'Disabling';
    const key = enqueueSnackbar(`${action} access...`, {
      variant: 'info',
      persist: true,
    });

    try {
      const result = await _onToggleAccess(options);
      closeSnackbar(key);
      if (result.data?.setIdentityUserAccess?.ok) {
        enqueueSnackbar(`${enable ? 'Enabled' : 'Disabled'} access successfully`, {
          variant: 'success',
        });
      } else {
        const errorMsg =
          result.data?.setIdentityUserAccess?.errors?.[0] || 'Unknown error';
        enqueueSnackbar(`Failed to ${action.toLowerCase()} access: ${errorMsg}`, {
          variant: 'error',
        });
      }
      return result;
    } catch (e) {
      closeSnackbar(key);
      enqueueSnackbar(
        `Failed to ${action.toLowerCase()} access: ${
          e instanceof Error ? e.message : 'Unknown error'
        }`,
        {variant: 'error'}
      );
      throw e;
    }
  };

  return {onToggleAccess, toggleAccessData, toggleAccessLoading};
};

export const useResetPassword = () => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [_onResetPassword, {data: resetPasswordData, loading: resetPasswordLoading}] =
    useMutation<ResetIdentityPassword, ResetIdentityPasswordVariables>(
      RESET_IDENTITY_PASSWORD_MUTATION,
      {
        refetchQueries: [GET_IDENTITY_USER_QUERY],
      }
    );

  const onResetPassword = async (
    options?: MutationFunctionOptions<
      ResetIdentityPassword,
      ResetIdentityPasswordVariables
    >
  ) => {
    closeSnackbar();
    const key = enqueueSnackbar('Resetting password...', {
      variant: 'info',
      persist: true,
    });

    try {
      const result = await _onResetPassword(options);
      closeSnackbar(key);
      if (result.data?.resetIdentityPassword?.ok) {
        enqueueSnackbar('Password reset successfully', {variant: 'success'});
      } else {
        const errorMsg =
          result.data?.resetIdentityPassword?.errors?.[0] || 'Unknown error';
        enqueueSnackbar(`Failed to reset password: ${errorMsg}`, {variant: 'error'});
      }
      return result;
    } catch (e) {
      closeSnackbar(key);
      enqueueSnackbar(
        `Failed to reset password: ${e instanceof Error ? e.message : 'Unknown error'}`,
        {variant: 'error'}
      );
      throw e;
    }
  };

  return {onResetPassword, resetPasswordData, resetPasswordLoading};
};

export const useResetMfa = () => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [_onResetMfa, {data: resetMfaData, loading: resetMfaLoading}] = useMutation<
    ResetIdentityMfa,
    ResetIdentityMfaVariables
  >(RESET_IDENTITY_MFA_MUTATION, {
    refetchQueries: [GET_IDENTITY_USER_QUERY],
  });

  const onResetMfa = async (
    options?: MutationFunctionOptions<ResetIdentityMfa, ResetIdentityMfaVariables>
  ) => {
    closeSnackbar();
    const key = enqueueSnackbar('Resetting MFA...', {variant: 'info', persist: true});

    try {
      const result = await _onResetMfa(options);
      closeSnackbar(key);
      if (result.data?.resetIdentityMfa?.ok) {
        enqueueSnackbar('MFA reset successfully', {variant: 'success'});
      } else {
        const errorMsg = result.data?.resetIdentityMfa?.errors?.[0] || 'Unknown error';
        enqueueSnackbar(`Failed to reset MFA: ${errorMsg}`, {variant: 'error'});
      }
      return result;
    } catch (e) {
      closeSnackbar(key);
      enqueueSnackbar(
        `Failed to reset MFA: ${e instanceof Error ? e.message : 'Unknown error'}`,
        {variant: 'error'}
      );
      throw e;
    }
  };

  return {onResetMfa, resetMfaData, resetMfaLoading};
};

export const useUserToggleGroup = () => {
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [_onToggleGroup, {data: toggleGroupData, loading: toggleGroupLoading}] =
    useMutation<IdentityUserSetGroup, IdentityUserSetGroupVariables>(
      IDENTITY_USER_SET_GROUP_MUTATION,
      {
        refetchQueries: [GET_IDENTITY_USER_QUERY],
      }
    );

  const onToggleGroup = async (
    options?: MutationFunctionOptions<IdentityUserSetGroup, IdentityUserSetGroupVariables>
  ) => {
    closeSnackbar();
    const group = options?.variables?.group;
    const action = options?.variables?.action;
    if (!group) {
      enqueueSnackbar('No group selected', {variant: 'error'});
      return;
    }
    if (!action) {
      enqueueSnackbar('Enabled/disabled hasnt been selected', {variant: 'error'});
      return;
    }
    const groupLabel = GROUP_LABELS[group];
    const actionText =
      action === IdentityGroupActionEnum.ADD ? 'Adding to' : 'Removing from';
    const key = enqueueSnackbar(`${actionText} ${groupLabel}...`, {
      variant: 'info',
      persist: true,
    });

    try {
      const result = await _onToggleGroup(options);
      closeSnackbar(key);
      if (result.data?.identityUserSetGroup?.ok) {
        enqueueSnackbar(`Successfully updated ${groupLabel} membership`, {
          variant: 'success',
        });
      } else {
        const errorMsg =
          result.data?.identityUserSetGroup?.errors?.[0] || 'Unknown error';
        enqueueSnackbar(`Failed to update ${groupLabel}: ${errorMsg}`, {
          variant: 'error',
        });
      }
      return result;
    } catch (e) {
      closeSnackbar(key);
      enqueueSnackbar(
        `Failed to update ${groupLabel}: ${
          e instanceof Error ? e.message : 'Unknown error'
        }`,
        {variant: 'error'}
      );
      throw e;
    }
  };

  return {onToggleGroup, toggleGroupData, toggleGroupLoading};
};

export const SingleIdentityPage: React.FC = () => {
  const {id} = useParams<{id: string}>();

  const {onToggleAccess, toggleAccessLoading} = useToggleUserAccess();
  const {onResetPassword, resetPasswordLoading} = useResetPassword();
  const {onResetMfa, resetMfaLoading} = useResetMfa();
  const {onToggleGroup, toggleGroupLoading} = useUserToggleGroup();

  const disableButtons =
    toggleAccessLoading || resetPasswordLoading || resetMfaLoading || toggleGroupLoading;

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);

  const {data, loading, error} = useQuery<GetIdentityUser, GetIdentityUserVariables>(
    GET_IDENTITY_USER_QUERY,
    {
      variables: {
        uuidOrEmail: id || '',
      },
      nextFetchPolicy: 'no-cache',
      fetchPolicy: 'no-cache',
      skip: !id,
    }
  );

  const user = data?.getIdentityUser?.user;

  if (loading) return <Loading />;

  if (error) {
    return <ErrorPage error={error} text="Unable to load identity user" />;
  }

  if (data?.getIdentityUser?.errors?.length) {
    return (
      <ErrorPage
        error={
          new Error(data.getIdentityUser.errors[0] || 'Unable to load identity user')
        }
        text="Unable to load identity user"
      />
    );
  }

  if (!user) {
    return <ErrorPage error={new Error('User not found')} text="User not found" />;
  }

  const isClient = user.groups?.includes('Client');
  const isAdviser = user.groups?.includes('Adviser');
  const isAdmin = user.groups?.includes('Admin');

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Link to="/identity">
          <Box mb={2} display="flex" alignItems="center">
            <ArrowBack sx={{mr: 1, color: themeColors.backgroundMuted}} />
            <Typography variant="body1" color={themeColors.backgroundMuted}>
              Back to Identity Users
            </Typography>
          </Box>
        </Link>
        <Panel title="Identity User Details" color={themeColors.lightGreen}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6} lg={4}>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Username
                </Typography>
                <Typography variant="body1">{user.username}</Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Email
                </Typography>
                <Typography variant="body1">{user.email}</Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Sub ID
                </Typography>
                <Typography variant="body1">{user.subId}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>

                <Box
                  display="flex"
                  alignItems="flex-start"
                  flexDirection="column"
                  gap={1}
                >
                  <ColoredChip
                    value={user.enabled ? 'Enabled' : 'Disabled'}
                    colorScheme={{
                      Enabled: themeColors.lightGreen,
                      Disabled: themeColors.lightAlert,
                    }}
                  />
                  <ColoredChip
                    value={user.status}
                    colorScheme={{
                      [UserStatusEnum.ARCHIVED]: themeColors.greyLight,
                      [UserStatusEnum.COMPROMISED]: themeColors.lightAlert,
                      [UserStatusEnum.CONFIRMED]: themeColors.lightGreen,
                      [UserStatusEnum.EXTERNAL_PROVIDER]: themeColors.lightBlue,
                      [UserStatusEnum.FORCE_CHANGE_PASSWORD]: themeColors.lightYellow,
                      [UserStatusEnum.RESET_REQUIRED]: themeColors.lightYellow,
                      [UserStatusEnum.UNCONFIRMED]: themeColors.lightYellow,
                      [UserStatusEnum.UNKNOWN]: themeColors.greyLight,
                    }}
                  />
                </Box>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Email Verified
                </Typography>
                <Typography variant="body1">
                  {user.emailVerified ? 'Verified' : 'Not verified'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  MFA Status
                </Typography>
                <Typography variant="body1">
                  {user.mfaEnabled ? `Enabled (${user.mfaSetting})` : 'Disabled'}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Groups
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                  {user.groups?.map((group: string) => {
                    const label = getGroupLabel(group);
                    return <Chip key={group} label={label} size="small" />;
                  }) || 'No groups'}
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Created At
                </Typography>
                <Typography variant="body1">
                  {user.createdAt ? new Date(user.createdAt).toLocaleString() : '-'}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" color="textSecondary">
                  Last Modified
                </Typography>
                <Typography variant="body1">
                  {user.lastModifiedAt
                    ? new Date(user.lastModifiedAt).toLocaleString()
                    : '-'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Panel>
      </Grid>
      <Grid item xs={12}>
        <Panel title="Actions" color={themeColors.lightBlue}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Tooltip
                  title={
                    user.enabled
                      ? 'Disable Cognito access for this user, including logging them out of oneview and client portal'
                      : 'Enable Cognito access for this user, allowing them to log in to oneview and client portal'
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      disabled={disableButtons}
                      color={user.enabled ? 'secondary' : 'primary'}
                      onClick={() => {
                        if (user.subId) {
                          if (
                            window.confirm(
                              `Are you sure you want to ${
                                user.enabled ? 'disable' : 'enable'
                              } access for ${user.username}?`
                            )
                          ) {
                            onToggleAccess({
                              variables: {enable: !user.enabled, subId: user.subId},
                            });
                          }
                        }
                      }}
                    >
                      {user.enabled ? 'Disable Access' : 'Enable Access'}
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip
                  title="Update the user's email address. Their login will change to the new email, but users do not get an email confirmation."
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      disabled={disableButtons}
                      onClick={() => setEmailDialogOpen(true)}
                    >
                      Update Email
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip title="Set a new temporary password for this user" arrow>
                  <span>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={disableButtons}
                      onClick={() => {
                        if (user.subId) {
                          if (
                            window.confirm('Are you sure you want to reset the password?')
                          ) {
                            onResetPassword({variables: {subId: user.subId}});
                          }
                        }
                      }}
                    >
                      Reset Password
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip
                  title={
                    !user.mfaEnabled
                      ? 'MFA must be enabled to reset it'
                      : 'SPP advisers only and if they have MFA enabled. This allows them to set up MFA up again.'
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      color="secondary"
                      disabled={disableButtons || !user.mfaEnabled}
                      onClick={() => {
                        if (user.subId) {
                          if (window.confirm('Are you sure you want to reset MFA?')) {
                            onResetMfa({variables: {subId: user.subId}});
                          }
                        }
                      }}
                    >
                      Reset MFA
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip
                  title="Add user to the Cognito Client group. Note, client setup is done automatically via the portal when an adviser clicks invite on a specific client."
                  arrow
                >
                  <span>
                    <Button
                      variant={isClient ? 'outlined' : 'contained'}
                      color={isClient ? 'error' : 'primary'}
                      disabled={disableButtons}
                      onClick={() => {
                        if (user.subId) {
                          onToggleGroup({
                            variables: {
                              subId: user.subId,
                              group: IdentityGroupEnum.CLIENT,
                              action: isClient
                                ? IdentityGroupActionEnum.REMOVE
                                : IdentityGroupActionEnum.ADD,
                            },
                          });
                        }
                      }}
                    >
                      {isClient
                        ? `Remove from ${GROUP_LABELS[IdentityGroupEnum.CLIENT]}`
                        : `Add to ${GROUP_LABELS[IdentityGroupEnum.CLIENT]}`}
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip
                  title='Standard administrator but not "admin". Can see all clients. To add someone as a admin admin, contact dev team.'
                  arrow
                >
                  <span>
                    <Button
                      variant={isAdmin ? 'outlined' : 'contained'}
                      color={isAdmin ? 'error' : 'primary'}
                      disabled={disableButtons}
                      onClick={() => {
                        if (user.subId) {
                          onToggleGroup({
                            variables: {
                              subId: user.subId,
                              group: IdentityGroupEnum.ADMIN,
                              action: isAdmin
                                ? IdentityGroupActionEnum.REMOVE
                                : IdentityGroupActionEnum.ADD,
                            },
                          });
                        }
                      }}
                    >
                      {isAdmin
                        ? `Remove from ${GROUP_LABELS[IdentityGroupEnum.ADMIN]}`
                        : `Add to ${GROUP_LABELS[IdentityGroupEnum.ADMIN]}`}
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip title="Adviser OneView user. Can only see their clients." arrow>
                  <span>
                    <Button
                      variant={isAdviser ? 'outlined' : 'contained'}
                      color={isAdviser ? 'error' : 'primary'}
                      disabled={disableButtons}
                      onClick={() => {
                        if (user.subId) {
                          onToggleGroup({
                            variables: {
                              subId: user.subId,
                              group: IdentityGroupEnum.ADVISER,
                              action: isAdviser
                                ? IdentityGroupActionEnum.REMOVE
                                : IdentityGroupActionEnum.ADD,
                            },
                          });
                        }
                      }}
                    >
                      {isAdviser
                        ? `Remove from ${GROUP_LABELS[IdentityGroupEnum.ADVISER]}`
                        : `Add to ${GROUP_LABELS[IdentityGroupEnum.ADVISER]}`}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Grid>

            <UpdateEmail
              open={emailDialogOpen}
              onClose={() => setEmailDialogOpen(false)}
              subId={user.subId}
              currentEmail={user.email}
              disabled={disableButtons}
            />
          </Grid>
        </Panel>
      </Grid>
      <Grid item xs={12}>
        <IdentityHistory subId={user.subId} />
      </Grid>
    </Grid>
  );
};
